import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import { Typography, Row, Col, Pagination, Tabs, message, Modal, DatePicker, Switch } from 'antd'
import { PlusOutlined, DownloadOutlined, UnorderedListOutlined } from '@ant-design/icons'

import { useMsal } from '@azure/msal-react'
import Table from '../../components/common/table/table'
import Button from '../../components/common/button/button'
import Spinner from '../../components/common/spinner/spinner'
import RfpVendorForm from './rfpForms/rfpvendorForm'
import DraftsComponent from './rfpDrafts/rfpDrafts'
import CostCenterModal from './costCenterModal/costCenterModal'
import RfpAttachmentModal from './rfpAttachments/rfpAttachmentModal'
import ApproverListModal from './approverListModal/approverListModal'
import { UserContext } from '../../context/userContext'
import { StoreContext } from '../../context/storeContext'
import {
  PAGE_SIZE_OPTIONS,
  fetchRfpData,
  fetchDraftsData,
  handleTabChange,
  handleCostCenterClick,
  handleAddRFP,
  handleFormCancel,
  handleFormSubmit,
  handleSaveAsDraft,
  handlePageChange,
  handleEditDraft,
  handleDeleteDraft,
  handleCostCenterModalClose,
  handleDownloadRFP,
  handlePaymentStatusChange,
  createEmptyState,
  createColumns,
  renderHeaderWithTooltip,
  getStatusColor,
  handleSearchRFP
} from './rfpUtils'
import './rfp.scss'

const { Title } = Typography

const RFP = () => {
  // State management
  const [loading, setLoading] = useState(true)
  const [draftsLoading, setDraftsLoading] = useState(false)
  const [reminderLoadingByRfpId, setReminderLoadingByRfpId] = useState({})
  const [activeTab, setActiveTab] = useState('rfp')
  const [rfpData, setRfpData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [drafts, setDrafts] = useState([])
  const [totalDrafts, setTotalDrafts] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [editingDraft, setEditingDraft] = useState(null)
  const [currentUserEmail, setCurrentUserEmail] = useState('')
  

  // Cost center modal state
  const [costCenterModalVisible, setCostCenterModalVisible] = useState(false)
  const [selectedCostCenterData, setSelectedCostCenterData] = useState(null)
  const [selectedVendorName, setSelectedVendorName] = useState('')

  // Attachment modal state
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false)
  const [selectedRfp, setSelectedRfp] = useState(null)
  const [fileList, setFileList] = useState([])
  const [attachmentLoading, setAttachmentLoading] = useState({
    uploadAttachment: false,
    previewAttachment: {},
    downloadAttachment: {},
    deleteAttachment: {}
  })

  // Approver list modal state
  const [approverListModalVisible, setApproverListModalVisible] = useState(false)

  const { instance: msalInstance, accounts } = useMsal()
  const { userRole } = useContext(UserContext)
  const { name: currentStoreName, id: currentStoreId } = useContext(StoreContext) || {}

  const [searchTerm, setSearchTerm] = useState('')
const [, setRfpIdSearchTerm] = useState('')

  // Download date range modal state
  const [downloadModalVisible, setDownloadModalVisible] = useState(false)
  const [downloadDateRange, setDownloadDateRange] = useState(null)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadAllStores, setDownloadAllStores] = useState(false)
const [, setIsSearchActive] = useState(false)

  // Get current user email
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const email = accounts[0].username || accounts[0].email
      setCurrentUserEmail(email)
    }
  }, [accounts])

  // Memoized fetch functions with bound parameters
  const boundFetchRfpData = useCallback(async (page = 1, limit = 10) => {
    await fetchRfpData(msalInstance, page, limit, setLoading, setRfpData, setTotalRecords)
  }, [msalInstance])

  const boundFetchDraftsData = useCallback(async (page = 1, limit = 10) => {
    await fetchDraftsData(msalInstance, page, limit, setDraftsLoading, setDrafts, setTotalDrafts)
  }, [msalInstance])

  // Effect for data loading based on active tab
  useEffect(() => {
    if (activeTab === 'rfp') {
      boundFetchRfpData(currentPage, pageSize)
    } else if (activeTab === 'drafts') {
      boundFetchDraftsData(currentPage, pageSize)
    }
  }, [currentPage, pageSize, activeTab, boundFetchRfpData, boundFetchDraftsData])

  // Refresh function for after approval/rejection
  const handleRefresh = useCallback(() => {
    if (activeTab === 'rfp') {
      boundFetchRfpData(currentPage, pageSize)
    } else if (activeTab === 'drafts') {
      boundFetchDraftsData(currentPage, pageSize)
    }
  }, [activeTab, currentPage, pageSize, boundFetchRfpData, boundFetchDraftsData])

  // Attachment Modal Handlers
  const handleAttachmentClick = useCallback((record) => {
    setSelectedRfp(record)
    setFileList([])
    setAttachmentModalVisible(true)
  }, [])

  const handleAttachmentModalClose = useCallback(() => {
    setAttachmentModalVisible(false)
    setSelectedRfp(null)
    setFileList([])
  }, [])

  const handleUploadFile = useCallback(async (files) => {
    try {
      setAttachmentLoading(prev => ({ ...prev, uploadAttachment: true }))

      // Import the API function
      const { handleRfpAttachments } = require('../../api/rfpApiService/rfpApiUtils')

      // Convert single file to array for uniform handling
      const fileArray = Array.isArray(files) ? files : [files]

      // Upload all files sequentially
      const uploadPromises = fileArray.map(file =>
        handleRfpAttachments(
          selectedRfp.id, // This is the _id from the API response
          'update', // Action type for upload
          null, // No attachment ID needed for upload
          file, // The file to upload
          msalInstance
        )
      )

      // Wait for all uploads to complete
      const responses = await Promise.all(uploadPromises)

      console.log(`${fileArray.length} file(s) uploaded successfully:`, responses)

      // Store the current RFP ID before closing
      const currentRfpId = selectedRfp.id

      // Close the modal temporarily
      handleAttachmentModalClose()

      // Refresh the appropriate data based on active tab
      if (activeTab === 'rfp') {
        await boundFetchRfpData(currentPage, pageSize)

        // Find the updated RFP record and reopen the modal
        setTimeout(() => {
          setRfpData(currentRfpData => {
            const updatedRecord = currentRfpData.find(rfp => rfp.id === currentRfpId)
            if (updatedRecord) {
              setSelectedRfp(updatedRecord)
              setAttachmentModalVisible(true)
            }
            return currentRfpData
          })
        }, 100)
      } else if (activeTab === 'drafts') {
        await boundFetchDraftsData(currentPage, pageSize)

        // Find the updated draft record and reopen the modal
        setTimeout(() => {
          setDrafts(currentDrafts => {
            const updatedRecord = currentDrafts.find(draft => draft.id === currentRfpId)
            if (updatedRecord) {
              setSelectedRfp(updatedRecord)
              setAttachmentModalVisible(true)
            }
            return currentDrafts
          })
        }, 100)
      }

      return responses
    } catch (error) {
      console.error('Error uploading file(s):', error)
      
      // Display appropriate error message
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
        message.error(`Failed to upload file: ${errorMessage}`)
      } else if (error.request) {
        message.error('Content is too large. Please reduce the file size and try again.')
      } else {
        message.error('Failed to upload file: ' + (error.message || 'Unknown error'))
      }
      
      throw error
    } finally {
      setAttachmentLoading(prev => ({ ...prev, uploadAttachment: false }))
    }
  }, [selectedRfp, msalInstance, activeTab, currentPage, pageSize, boundFetchRfpData, boundFetchDraftsData, handleAttachmentModalClose])

  const handlePreviewDocument = useCallback(async (doc) => {
    try {
      setAttachmentLoading(prev => ({
        ...prev,
        previewAttachment: { ...prev.previewAttachment, [doc._id]: true }
      }))

      // Import the API function
      const { generateRfpPresignedUrl } = require('../../api/rfpApiService/rfpApiUtils')

      // The API response has s3Location field instead of attachmentS3Path
      const s3Path = doc.s3Location || doc.attachmentS3Path

      // Generate presigned URL for the document
      const presignedUrl = await generateRfpPresignedUrl(s3Path, msalInstance)

      console.log('Previewing document:', doc.filename || doc.originalFileName)

      // Open in new tab
      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error('Error previewing document:', error)
      throw error
    } finally {
      setAttachmentLoading(prev => ({
        ...prev,
        previewAttachment: { ...prev.previewAttachment, [doc._id]: false }
      }))
    }
  }, [msalInstance])

  const handleDownloadDocument = useCallback(async (doc) => {
    try {
      setAttachmentLoading(prev => ({
        ...prev,
        downloadAttachment: { ...prev.downloadAttachment, [doc._id]: true }
      }))

      // Import the API function
      const { generateRfpPresignedUrl } = require('../../api/rfpApiService/rfpApiUtils')

      // The API response has s3Location field instead of attachmentS3Path
      const s3Path = doc.s3Location || doc.attachmentS3Path

      // Generate presigned URL for the document
      const presignedUrl = await generateRfpPresignedUrl(s3Path, msalInstance)

      const fileName = doc.filename || doc.originalFileName || 'download'
      console.log('Downloading document:', fileName)

      // Create a temporary link to download the file
      const link = document.createElement('a')
      link.href = presignedUrl
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading document:', error)
      throw error
    } finally {
      setAttachmentLoading(prev => ({
        ...prev,
        downloadAttachment: { ...prev.downloadAttachment, [doc._id]: false }
      }))
    }
  }, [msalInstance])

  const handleDeleteDocument = useCallback(async (docId) => {
    try {
      setAttachmentLoading(prev => ({
        ...prev,
        deleteAttachment: { ...prev.deleteAttachment, [docId]: true }
      }))

      // Import the API function
      const { handleRfpAttachments } = require('../../api/rfpApiService/rfpApiUtils')

      // Call the API to delete the attachment
      // Use the _id from the RFP record as txnId
      // Use the _id from the attachment object as attachmentId
      const response = await handleRfpAttachments(
        selectedRfp.id, // This is the _id from the API response
        'delete', // Action type for delete
        docId, // This is the attachment's _id (docId parameter is actually the attachment _id)
        null, // No file needed for delete
        msalInstance
      )

      console.log('Document deleted successfully:', response)

      // Store the current RFP ID before closing
      const currentRfpId = selectedRfp.id

      // Close the modal temporarily
      handleAttachmentModalClose()

      // Refresh the appropriate data based on active tab
      if (activeTab === 'rfp') {
        await boundFetchRfpData(currentPage, pageSize)

        // Find the updated RFP record and reopen the modal
        setTimeout(() => {
          setRfpData(currentRfpData => {
            const updatedRecord = currentRfpData.find(rfp => rfp.id === currentRfpId)
            if (updatedRecord) {
              setSelectedRfp(updatedRecord)
              setAttachmentModalVisible(true)
            }
            return currentRfpData
          })
        }, 100)
      } else if (activeTab === 'drafts') {
        await boundFetchDraftsData(currentPage, pageSize)

        // Find the updated draft record and reopen the modal
        setTimeout(() => {
          setDrafts(currentDrafts => {
            const updatedRecord = currentDrafts.find(draft => draft.id === currentRfpId)
            if (updatedRecord) {
              setSelectedRfp(updatedRecord)
              setAttachmentModalVisible(true)
            }
            return currentDrafts
          })
        }, 100)
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      throw error
    } finally {
      setAttachmentLoading(prev => ({
        ...prev,
        deleteAttachment: { ...prev.deleteAttachment, [docId]: false }
      }))
    }
  }, [selectedRfp, msalInstance, activeTab, currentPage, pageSize, boundFetchRfpData, boundFetchDraftsData, handleAttachmentModalClose])

  // Payment Status Change Handler
  // const onPaymentStatusChange = useCallback(async (record, newStatus) => {
  //   await handlePaymentStatusChange(record, newStatus, msalInstance, handleRefresh, currentUserEmail)
  // }, [msalInstance, handleRefresh, currentUserEmail])
  const onPaymentStatusChange = useCallback(async (record, newStatus) => {
    await handlePaymentStatusChange(
      record,
      newStatus,
      msalInstance,
      handleRefresh,
      userRole // <-- PASS ROLE
    )
  }, [msalInstance, handleRefresh, userRole])

  // Event handlers with bound parameters
  const onTabChange = useCallback((key) => {
    handleTabChange(key, setActiveTab, setCurrentPage, pageSize, boundFetchDraftsData, boundFetchRfpData)
  }, [pageSize, boundFetchDraftsData, boundFetchRfpData])

  const onCostCenterClick = useCallback((record) => {
    handleCostCenterClick(record, setSelectedCostCenterData, setSelectedVendorName, setCostCenterModalVisible)
  }, [])

  const onAddRFP = useCallback(() => {
    handleAddRFP(setEditingDraft, setIsFormVisible)
  }, [])

  const onFormCancel = useCallback(() => {
    handleFormCancel(setEditingDraft, setIsFormVisible)
  }, [])

  const onFormSubmit = useCallback(async (formData) => {
    await handleFormSubmit(
      formData,
      currentPage,
      pageSize,
      editingDraft,
      activeTab,
      setRfpData,
      setTotalRecords,
      setDrafts,
      setTotalDrafts,
      setIsFormVisible,
      setEditingDraft,
      setCurrentPage,
      boundFetchRfpData,
      boundFetchDraftsData
    )
  }, [currentPage, pageSize, editingDraft, activeTab, boundFetchRfpData, boundFetchDraftsData])

  const onSaveAsDraft = useCallback(async (formData) => {
    await handleSaveAsDraft(
      formData,
      editingDraft,
      activeTab,
      pageSize,
      setDrafts,
      setTotalDrafts,
      setIsFormVisible,
      setEditingDraft,
      boundFetchDraftsData
    )
  }, [editingDraft, activeTab, pageSize, boundFetchDraftsData])

  const onPageChange = useCallback(async (page, size) => {
    await handlePageChange(
      page,
      size,
      pageSize,
      activeTab,
      setPageSize,
      setCurrentPage,
      boundFetchDraftsData,
      boundFetchRfpData
    )
  }, [pageSize, activeTab, boundFetchDraftsData, boundFetchRfpData])

  const onEditDraft = useCallback(async (draft) => {
    await handleEditDraft(draft, setEditingDraft, setIsFormVisible, msalInstance)
  }, [msalInstance])

  const onDeleteDraft = useCallback(async (draftId) => {
    await handleDeleteDraft(
      draftId,
      activeTab,
      currentPage,
      pageSize,
      setDrafts,
      setTotalDrafts,
      boundFetchDraftsData,
      msalInstance
    )
  }, [activeTab, currentPage, pageSize, boundFetchDraftsData, msalInstance])

  const onCostCenterModalClose = useCallback(() => {
    handleCostCenterModalClose(
      setCostCenterModalVisible,
      setSelectedCostCenterData,
      setSelectedVendorName
    )
  }, [])

  const onDownloadButtonClick = useCallback(() => {
    setDownloadDateRange(null)
    setDownloadAllStores(false)
    setDownloadModalVisible(true)
  }, [])

  const onDownloadConfirm = useCallback(async () => {
    setDownloadLoading(true)
    try {
      await handleDownloadRFP(msalInstance, downloadDateRange, downloadAllStores)
      setDownloadModalVisible(false)
      setDownloadDateRange(null)
      setDownloadAllStores(false)
    } finally {
      setDownloadLoading(false)
    }
  }, [msalInstance, downloadDateRange, downloadAllStores])

  const onApproverListClick = useCallback(() => {
    setApproverListModalVisible(true)
  }, [])

  const onApproverListModalClose = useCallback(() => {
    setApproverListModalVisible(false)
  }, [])
  const onRfpIdSearch = useCallback(async (value) => {
  if (!value?.trim()) {
    // Reset to normal list
    setRfpIdSearchTerm('')
    setIsSearchActive(false)
    setCurrentPage(1)
    await boundFetchRfpData(1, pageSize)
    return
  }

  try {
    setRfpIdSearchTerm(value)
    setIsSearchActive(true)
    setLoading(true)

    const { searchRFP } = require('../../api/rfpApiService/rfpApiUtils')
    const response = await searchRFP(msalInstance, value.trim())

    if (response?.data) {
      const { transformApiData } = require('./rfpUtils')
      const transformed = transformApiData(response.data)
      setRfpData(transformed)
      setTotalRecords(transformed.length)
    } else {
      setRfpData([])
      setTotalRecords(0)
    }
  } catch (error) {
    console.error('Search error:', error)
    message.error('Failed to search RFP.')
  } finally {
    setLoading(false)
  }
}, [msalInstance, pageSize, boundFetchRfpData])
const onSearch = useCallback(async (value) => {
  if (!value?.trim()) {
    // If cleared, reload normal data
    setIsSearchActive(false)
    setSearchTerm('')
    await boundFetchRfpData(1, pageSize)
    setCurrentPage(1)
    return
  }
  setSearchTerm(value)
  await handleSearchRFP(
    value,
    msalInstance,
    setLoading,
    setRfpData,
    setTotalRecords,
    setIsSearchActive
  )
}, [msalInstance, pageSize, boundFetchRfpData])

const onSearchClear = useCallback(async () => {
  setSearchTerm('')
  setIsSearchActive(false)
  setCurrentPage(1)
  await boundFetchRfpData(1, pageSize)
}, [boundFetchRfpData, pageSize])

const onSendReminder = useCallback((record) => {
  Modal.confirm({
    title: 'Send reminder?',
    content: 'Send a follow-up approval reminder for this RFP?',
    okText: 'Send',
    cancelText: 'Cancel',
    onOk: async () => {
      const reminderKey = record.id || record.rfpId
      let canProceed = true

      setReminderLoadingByRfpId((prev) => {
        if (prev[reminderKey]) {
          canProceed = false
          return prev
        }
        return { ...prev, [reminderKey]: true }
      })

      if (!canProceed) {
        return
      }

      try {
        const { sendRfpApprovalReminder } = require('../../api/rfpApiService/rfpApiUtils')
        const response = await sendRfpApprovalReminder(record.rfpId, currentStoreId, msalInstance)

        const reminderSentTo = response?.data?.reminderSentTo
        const recipientCount = Array.isArray(reminderSentTo)
          ? reminderSentTo.length
          : typeof reminderSentTo === 'number'
            ? reminderSentTo
            : typeof reminderSentTo === 'string' && reminderSentTo.trim()
              ? reminderSentTo.split(',').filter(Boolean).length
              : 0

        const backendMessage = response?.message || 'RFP reminder email sent successfully'
        if (recipientCount > 0) {
          message.success(`${backendMessage}. Reminder sent to ${recipientCount} approver(s)`)
        } else {
          message.success(backendMessage)
        }
      } catch (error) {
        console.error('Error sending RFP reminder email:', error)
        message.error(error.response?.data?.message || 'Failed to send reminder email. Please retry.')
      } finally {
        setReminderLoadingByRfpId((prev) => ({ ...prev, [reminderKey]: false }))
      }
    }
  })
}, [msalInstance, currentStoreId])

  // Memoized UI elements
const columns = useMemo(() =>
  createColumns(
    currentPage,
    pageSize,
    renderHeaderWithTooltip,
    getStatusColor,
    onCostCenterClick,
    msalInstance,
    handleRefresh,
    handleAttachmentClick,
    currentUserEmail,
    accounts?.[0]?.name || '',
    onPaymentStatusChange,
    userRole,
    onRfpIdSearch,
    onSendReminder,
    reminderLoadingByRfpId
  ),
  [currentPage, pageSize, onCostCenterClick, msalInstance, handleRefresh,
   handleAttachmentClick, currentUserEmail, accounts, onPaymentStatusChange, userRole, onRfpIdSearch,
   onSendReminder, reminderLoadingByRfpId]
)

  const emptyState = useMemo(() => createEmptyState(activeTab), [activeTab])

  const loadingState = useMemo(() => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px'
    }}>
      <Spinner size="large" />
    </div>
  ), [])

  // Current user object for attachment modal
  const currentUser = useMemo(() => ({
    username: currentUserEmail,
    name: accounts?.[0]?.name || ''
  }), [currentUserEmail, accounts])

  // Tab items configuration
  const tabItems = useMemo(() => [
    {
      key: 'rfp',
      label: 'RFP',
      children: (
        <div style={{ marginTop: '24px' }}>
          <Table
            columns={columns}
            dataSource={loading ? [] : rfpData}
            rowKey="id"
            scroll={{ x: 1800, y: 'calc(100vh - 500px)' }}
            pagination={false}
            className="rfp-page__table"
            sticky={{ offsetHeader: 0 }}
            bordered={false}
            size="middle"
            locale={{
              emptyText: loading ? loadingState : emptyState
            }}
          />

          {!loading && (
            <div className="rfp-page__pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecords}
                showSizeChanger={true}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} RFPs`
                }
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={onPageChange}
                onShowSizeChange={onPageChange}
              />
            </div>
          )}
        </div>
      )
    },
    {
      key: 'drafts',
      label: 'Drafts',
      children: (
        <DraftsComponent
          drafts={drafts}
          onEditDraft={onEditDraft}
          onDeleteDraft={onDeleteDraft}
          loading={draftsLoading}
          totalRecords={totalDrafts}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onRefreshDrafts={() => boundFetchDraftsData(currentPage, pageSize)}
          currentUserEmail={currentUserEmail}
          onCostCenterClick={onCostCenterClick}
        />
      )
    }
  ], [
    columns,
    loading,
    rfpData,
    loadingState,
    emptyState,
    currentPage,
    pageSize,
    totalRecords,
    onPageChange,
    drafts,
    onEditDraft,
    onDeleteDraft,
    draftsLoading,
    totalDrafts,
    boundFetchDraftsData,
    currentUserEmail,
    onCostCenterClick,
      searchTerm,
  onSearch,
  onSearchClear,
  onApproverListClick 
  ])

  return (
    <div className="rfp-page">
      <Row className="rfp-page__header">
        <Col span={24}>
          <Title level={4} className="rfp-page__title">
            RFP - Request for Payment
          </Title>
          <hr />
        </Col>
      </Row>

      <div className="rfp-page__tabs-actions" style={{ marginTop: '24px' }}>
        <style>
          {`
            .custom-tabs .ant-tabs-tab {
              color: #8c8c8c !important;
              font-weight: 500;
            }
            .custom-tabs .ant-tabs-tab-active {
              color: #000000 !important;
              font-weight: 600;
            }
            .custom-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
              color: #ad0270 !important;
              text-shadow: none !important;
            }
            .custom-tabs .ant-tabs-tab:hover {
              color: #ad0270!important;
            }
            .custom-tabs .ant-tabs-ink-bar {
              background-color:  #ad0270!important;
              height: 3px !important;
            }
            .custom-tabs .ant-tabs-nav::before {
              border-bottom: 1px solid #f0f0f0 !important;
            }
          `}
        </style>
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          items={tabItems}
          size="large"
          className="custom-tabs"
          tabBarStyle={{
            marginBottom: 0,
            borderBottom: 'none'
          }}
tabBarExtraContent={
  activeTab === 'rfp' ? (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
      <Button
        icon={<UnorderedListOutlined />}
        onClick={onApproverListClick}
      >
        Approver List
      </Button>
      <Button
        icon={<DownloadOutlined />}
        onClick={onDownloadButtonClick}
        disabled={loading}
      >
        Download
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={onAddRFP}
        className="rfp-page__add-button"
      >
        Create New RFP
      </Button>
    </div>
  ) : null
}
        />
      </div>

      <RfpVendorForm
        visible={isFormVisible}
        onCancel={onFormCancel}
        onSubmit={onFormSubmit}
        onSaveAsDraft={onSaveAsDraft}
        editingDraft={editingDraft}
      />

      <CostCenterModal
        visible={costCenterModalVisible}
        onClose={onCostCenterModalClose}
        costCenterData={selectedCostCenterData}
        vendorName={selectedVendorName}
      />

      <RfpAttachmentModal
        visible={attachmentModalVisible}
        onClose={handleAttachmentModalClose}
        rfp={selectedRfp}
        fileList={fileList}
        setFileList={setFileList}
        actionLoading={attachmentLoading}
        currentUser={currentUser}
        handleUploadFile={handleUploadFile}
        handlePreviewDocument={handlePreviewDocument}
        handleDownloadDocument={handleDownloadDocument}
        handleDeleteDocument={handleDeleteDocument}
      />

      <ApproverListModal
        visible={approverListModalVisible}
        onClose={onApproverListModalClose}
      />

      <Modal
        title="Download RFP List"
        open={downloadModalVisible}
        onOk={onDownloadConfirm}
        onCancel={() => { setDownloadModalVisible(false); setDownloadDateRange(null); setDownloadAllStores(false) }}
        okText={downloadAllStores ? 'Download All Stores' : `Download ${currentStoreName || 'Selected Store'}`}
        cancelText="Cancel"
        confirmLoading={downloadLoading}
        okButtonProps={{ icon: <DownloadOutlined />, className: 'app-button app-button-primary' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>Include All Stores</span>
          <Switch
            checked={downloadAllStores}
            onChange={setDownloadAllStores}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </div>
        {downloadAllStores && (
          <p style={{ marginBottom: 8, color: '#ad0270', fontSize: 12 }}>RFPs of all stores will be included in the export.</p>
        )}
        <p style={{ marginBottom: 8 }}>Filter by date range (optional):</p>
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          format="DD-MM-YYYY"
          value={downloadDateRange}
          onChange={(dates) => setDownloadDateRange(dates)}
          placeholder={['Start Date', 'End Date']}
          allowClear
        />
        {!downloadDateRange && (
          <p style={{ marginTop: 8, color: '#888', fontSize: 12 }}>No date filter applied — all records will be exported.</p>
        )}
      </Modal>
    </div>
  )
}

export default RFP